import axios, { AxiosRequestConfig, AxiosRequestHeaders } from 'axios';
import layer from '@/utils/layer';
import serviceConfig from './config';
import history from '@/utils/history';
import { clearLocalStorage } from '@/utils';

export const instance = axios.create({
  baseURL: serviceConfig.baseURL
});

const axiosErrorCode = new Map([
  ['ERR_NETWORK', '网络请求超时']
]);


// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function transJsonToFormdata(params: any) {
  if (params instanceof FormData) {
    return params;
  }
  try {
    const fd = new FormData();
    Object.keys(params).forEach((key) => {
      fd.append(key, params[key]);
    });
    return fd;
  } catch (e) {
    return params;
  }
}

instance.interceptors.request.use(
  function (config) {
    const userStore = JSON.parse(localStorage.getItem('userInfo') || '{}');
    const token = localStorage.getItem('token') as string;
    const headers = config.headers as AxiosRequestHeaders ;
    headers['Auth-JWT'] = token;
    if (userStore.userType === 'TEACHER') {
      headers['aware_organization_uid'] = userStore?.currentSchool?.id || localStorage.getItem('schoolId') as string;
    }
    return config;
  }
);

const backToLoginPage = () => {
  history.replace('/login');
};
let tokenUpdating = false;
const setNewToken = async () => {
  const res = await instance.get('/index/login/byrft', {
    params: {
      'Auth-JWT': localStorage.getItem('token'),
      'Auth-JWT-REFRESH': localStorage.getItem('refreshToken')
    }
  });
  const token = res.headers['auth-jwt'];
  const refreshToken = res.headers['auth-jwt-refresh'];
  localStorage.setItem('refreshToken', refreshToken);
  localStorage.setItem('token', token);
  return token;
};
instance.interceptors.response.use(
  async (response) => {
    const code = response.data.code;
    if (['FAILURE'].includes(code)) {
      layer.error(response.data.msg);
      return false;
    }
    if (['NOPERMISSION'].includes(code)) {
      history.replace('/nopermission');
    }
    if (['TOKEN_KICKOUT'].includes(code)) {
      const l = document.querySelector('.accountAlert');
      !l && layer.alert({
        content: '您的账号已在另一地点登录',
        className: 'accountAlert',
        onOk: () => {
          clearLocalStorage();
          backToLoginPage();
        }
      });
      return false;
    }
    if (['TOKEN_INVALID', 'REFRESH_TOKEN_INVALID'].includes(code)) {
      const l = document.querySelector('.accountAlert');
      !l && layer.alert({
        content: '登录已失效',
        className: 'accountAlert',
        onOk: () => {
          clearLocalStorage();
          backToLoginPage();
        }
      });
    }
    if (['TOKEN_LACK'].includes(code)) {
      backToLoginPage();
    }
    if (['TOKEN_EXPIRED'].includes(code)) {
      const config = response.config;
      const refreshJwt = localStorage.getItem('refreshToken');
      if (!tokenUpdating) {
        tokenUpdating = true;
        if (refreshJwt) {
          const newToken = await setNewToken();
          tokenUpdating = false;
          const headers = config.headers as AxiosRequestHeaders;
          headers['Auth-JWT'] = newToken;
          const res = await axios.request(config);
          return res;
        } else {
          tokenUpdating = false;
          // location.href = '/';
          return false;
        }
      } else {
        await new Promise(resolve => {
          const interval = setInterval(() => {
            if (!tokenUpdating) {
              resolve(true);
              clearInterval(interval);
            }
          });
        });
        const headers = config.headers as AxiosRequestHeaders;
        headers['Auth-JWT'] = localStorage.getItem('token') as string;
        const res = await axios.request(config);
        return res;
      }
    }
    return response;
  },
  (error) => {
    // 接口报错
    // eslint-disable-next-line no-console
    console.log(error);
    // layer.error(`${error?.config?.url}, 错误代码: ${error?.response?.status}`);
    layer.error(axiosErrorCode.get(error.code) || `错误类型：${error.code}`);
    return Promise.reject(error.response);
  }
);

export interface AxiosRequest {
  <T>(url: string, params?: unknown, config?: AxiosRequestConfig): Promise<CustomSuccessData<T>>;
}

const get: AxiosRequest = async (url, params, config) => {
  const response = await instance.get(url, { params, ...config });
  return response.data;
};

const post: AxiosRequest = async (url, data, config) => {
  const response = await instance.post(url, transJsonToFormdata(data), {
    ...config,
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
  return response.data;
};

const postJSON: AxiosRequest = async (url, data, config) => {
  const response = await instance.post(url, data, {
    ...config,
    headers: {
      'Content-Type': 'application/json'
    }
  });
  return response.data;
};

const put: AxiosRequest = async (url, data, config) => {
  const response = await instance.put(url, transJsonToFormdata(data), {
    ...config,
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
  return response.data;
};

const putJSON: AxiosRequest = async (url, data, config) => {
  const response = await instance.put(url, data, { ...config });
  return response.data;
};

const del: AxiosRequest = async (url, params, config) => {
  const response = await instance.delete(url, { params, ...config });
  return response.data;
};

export default {
  get,
  post,
  postJSON,
  put,
  putJSON,
  del
};
